import { ChatChannelPayload } from '@interfaces/api/payloads/chatChannelPayload';
import { UserToken } from '@interfaces/models/userToken';
import Environment from '@config/index';
import ChatApiService from '@services/chat-service';
import logger from '@helpers/utils/logger/client';
import dayjs from 'dayjs';
import React, { FCWithChildren, useContext, useEffect, useRef, useState } from 'react';
import {
  APIResponse,
  Channel as StreamChannel,
  DefaultGenerics,
  FormatMessageResponse,
  MessageBase,
  SendMessageAPIResponse,
  StreamChat,
  User,
  Event as StreamEvent,
} from 'stream-chat';
import { Product } from '@interfaces/models/product';
import useUser from '@hooks/user/use-user';
import { initialiseStreamChannel } from '@helpers/utils/chat/stream-cucha';
import { Channel as CuchaChannel, User as CuchaUser } from '@interfaces/api/responses/cucha-chat-channel';
import { isDefined } from '@helpers/utils/type-utils';
import { getDifferenceInHoursRounded } from '@helpers/utils/date-utils';
import { Shipping } from '@interfaces/models/shipping';

type ExtendedStreamEvent = StreamEvent & { expiration?: string };

type Message = {
  key: string;
  value: MessageBase[];
};

export type Mode = 'popup' | 'inline';

type ChatProviderProps = {
  product: Product;
  mode?: Mode;
  shipping?: Shipping;
  errorChatId?: string;
};

type Ban = {
  global: {
    banned: boolean;
    banDuration?: number;
    expiry?: Date;
  };
  channel: {
    banned: boolean;
  };
};

type ChatContextState = {
  banUserFromChannel: (channel: StreamChannel, userId: User['id']) => Promise<APIResponse>;
  channel: StreamChannel;
  setChannel: React.Dispatch<React.SetStateAction<StreamChannel>>;
  channelId: StreamChannel['id'];
  channelError: boolean;
  tokenError: boolean;
  buyerId: string;
  setChannelId: React.Dispatch<React.SetStateAction<string>>;
  chatWrapper: React.MutableRefObject<HTMLDivElement> | null;
  createChannel: (payload: ChatChannelPayload) => Promise<CuchaChannel>;
  chatLogout: () => Promise<void>;
  connectionState: boolean;
  setConnectionState: React.Dispatch<React.SetStateAction<boolean>>;
  handleSendingMessage: (channel: StreamChannel, text: MessageBase['text']) => void;
  init: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  msgBeenRead: boolean;
  setMsgBeenRead: React.Dispatch<React.SetStateAction<boolean>>;
  messagesEnding: React.MutableRefObject<HTMLDivElement> | null;
  markAsRead: (channel: StreamChannel) => Promise<void>;
  unbanUserFromChannel: (channel: StreamChannel, userId: User['id']) => Promise<APIResponse>;
  streamChat: StreamChat;
  transformMessages: (value: FormatMessageResponse[], ...args: unknown[]) => Message[];
  sendMessage: (channel: StreamChannel, text: MessageBase['text']) => void;
  scrollToBottom: (mode?: Mode) => void;
  senderBan: Ban;
  setSenderBan: React.Dispatch<React.SetStateAction<Ban>>;
  receiverBan: Ban;
  translateMessage: (messageID: MessageBase['id'], locale: string) => Promise<void>;
  translateMessagePromise: (messageID: MessageBase['id'], locale: string) => Promise<unknown>;
  tempStatus: {
    NO_NETWORK: string;
    LINK: string;
    BANNED: string;
    SENDING: string;
  };
  product: Product;
  mode: Mode;
  isSeller: boolean;
  buyerLocation: string;
  isProductMissing: boolean;
  isProductProhibited: boolean;
  errorChatId?: string;
};

const ChatContext = React.createContext<ChatContextState>(null);

const ChatProvider: FCWithChildren<ChatProviderProps> = (props): React.JSX.Element => {
  const { product, mode = 'popup', shipping, children, errorChatId } = props;

  const { user } = useUser();

  const [streamChat, setStreamChat] = useState<StreamChat>(null);
  const [channel, setChannel] = useState<StreamChannel>(null);
  const [channelId, setChannelId] = useState<StreamChannel['id']>(null);
  const [connectionState, setConnectionState] = useState<boolean>(false);
  const [channelError, setChannelError] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgBeenRead, setMsgBeenRead] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const chatWrapper = useRef<HTMLDivElement>(null);
  const messagesEnding = useRef<HTMLDivElement>(null);
  // These states should be displayed in the content
  const [senderBan, setSenderBan] = useState<Ban>({
    global: { banned: false },
    channel: { banned: false },
  });
  const [receiverBan, setReceiverBan] = useState<Ban>({
    global: { banned: false },
    channel: { banned: false },
  });
  const [buyerLocation, setBuyerLocation] = useState<string>(null);
  const [buyerId, setBuyerId] = useState<string>();
  const seller = product?.seller;
  const isSeller = seller?.id === user?.id;
  const isProductMissing = !product?.id;
  const isProductProhibited = shipping?.prohibited;

  const tempStatus = {
    NO_NETWORK: 'no-network',
    LINK: 'link',
    BANNED: 'banned',
    SENDING: 'sending',
  };

  const defaultMessage = {
    type: '',
    html: '',
  };

  const tokenProvider = (): Promise<UserToken> => {
    return ChatApiService.refreshToken();
  };

  // connect to VC api. VC api checks if channel exists otherwise create a new channel
  const createChannel = async (payload: ChatChannelPayload): Promise<CuchaChannel> => {
    const response = await ChatApiService.createChannel(payload);

    if (isDefined(response?.data)) {
      return response.data;
    }

    throw new Error(`Cannot create or find channel.`);
  };

  const banUserFromChannel = (channel: StreamChannel, userId: User['id']): Promise<APIResponse> => {
    return channel.banUser(userId, {}).then((res) => {
      setReceiverBan({
        ...receiverBan,
        channel: {
          banned: true,
        },
      });
      return res;
    });
  };

  const unbanUserFromChannel = (channel: StreamChannel, userId: User['id']): Promise<APIResponse> => {
    return channel.unbanUser(userId).then((res) => {
      setReceiverBan({
        ...receiverBan,
        channel: {
          banned: false,
        },
      });
      return res;
    });
  };

  const sendMessage = async (channel: StreamChannel, text: MessageBase['text']): Promise<void> => {
    await channel.sendMessage({ text }).then((message: SendMessageAPIResponse) => message);
  };

  const translateMessage = async (messageID: MessageBase['id'], locale: string): Promise<void> => {
    await streamChat?.translateMessage(messageID, locale).then((response) => response.message);
  };

  const translateMessagePromise = async (messageID: MessageBase['id'], locale: string): Promise<unknown> => {
    return streamChat?.translateMessage(messageID, locale).then((response) => response.message);
  };

  const markAsRead = async (channel: StreamChannel): Promise<void> => {
    await channel.markRead();
  };

  const chatLogout = async (): Promise<void> => {
    if (streamChat?.user?.online) {
      setConnectionState(false);
      setChannel(null);
      setMessages(null);

      try {
        await streamChat?.disconnectUser();
      } catch (error) {
        throw new Error(error.message);
      }
    }
  };

  const diffInHour = (date1: string, date2: string): number => {
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);

    return d1.diff(d2, 'hour');
  };

  const transformMessages = (value: FormatMessageResponse[] | MessageBase<DefaultGenerics>[]): Message[] => {
    const formatted = {};

    if (value.length > 0) {
      let messageToCheckWith = value[0];

      value.forEach((msg: FormatMessageResponse | MessageBase<DefaultGenerics>, index: number) => {
        if (diffInHour(msg.created_at.toString(), messageToCheckWith.created_at.toString()) > 1) {
          messageToCheckWith = value[index];
        }

        const dateStr = dayjs(messageToCheckWith.created_at.toString()).format();
        formatted[dateStr] = formatted[dateStr] ? [...formatted[dateStr], msg] : [msg];
      });
    }

    return Object.keys(formatted).map((key) => {
      return { key, value: formatted[key] };
    });
  };

  const scrollToBottom = (mode?: Mode): void => {
    if (mode === 'inline' && chatWrapper.current) {
      chatWrapper.current.scrollTop = chatWrapper.current?.scrollHeight;
      return;
    }
    messagesEnding.current?.scrollIntoView();
  };

  const formatMessage = (text: MessageBase['text'], status: string): MessageBase<DefaultGenerics> => {
    return {
      ...defaultMessage,
      text,
      status,
      id: dayjs().format(),
      created_at: dayjs().format(),
      updated_at: dayjs().format(),
      user: streamChat.user,
    };
  };

  const selectTempStatus = (err): string => {
    let tempStatusToSend = tempStatus.NO_NETWORK;
    if (err?.code === 17) {
      tempStatusToSend = err.message.includes('is not allowed to perform action AddLinks')
        ? tempStatus.LINK
        : tempStatus.BANNED;
    }
    return tempStatusToSend;
  };

  const handleSendingMessage = (channel: StreamChannel, text: MessageBase['text']): void => {
    setMsgBeenRead(false);

    if (messages.length > 0) {
      messages[messages.length - 1].value.push(formatMessage(text, tempStatus.SENDING));
      setMessages([...messages]);
      scrollToBottom(mode);
    } else {
      setMessages(transformMessages([formatMessage(text, tempStatus.SENDING)]));
    }

    sendMessage(channel, text)
      .then(() => {})
      .catch((err) => {
        if (messages.length > 0) {
          const latestMessages = messages[messages.length - 1].value;
          latestMessages.pop();
          latestMessages.push(formatMessage(text, selectTempStatus(err)));
          setMessages([...messages]);
          scrollToBottom(mode);
        } else {
          setMessages(transformMessages([formatMessage(text, selectTempStatus(err))]));
        }
      });
  };

  const getBuyerId = () => {
    const url = new URL(window.location.href);
    // if its chat page
    if (/\/chat\b/.test(url.pathname)) {
      const params = new URLSearchParams(url.search);
      return params.get('id')?.split('-').shift();
    }
    return user?.id;
  };

  const hasGlobalBans = (streamChannel: StreamChannel): boolean => {
    const members = streamChannel.state.members;
    const globalBanUsers = Object.values(members)
      .filter((member) => member.user.banned)
      .map((member) => member.user.id);

    return globalBanUsers.length > 0;
  };

  const updateChannelBanStateFromStreamCall = (streamChannel: StreamChannel): void => {
    const channelBannedMembers = Object.values(streamChannel.state.members).filter((member) => member.banned);

    setSenderBan({
      ...senderBan,
      channel: {
        banned: channelBannedMembers.some((member) => member.user.id === user.id),
      },
    });

    setReceiverBan({
      ...receiverBan,
      channel: {
        banned: channelBannedMembers.some((member) => member.user.id !== user.id),
      },
    });
  };

  const updateGlobalBanStateFromCuchaCall = (cuchaChannel: CuchaChannel) => {
    const isTheSenderTheBuyer = cuchaChannel.buyer.id === user?.id;

    const sender: CuchaUser = isTheSenderTheBuyer ? cuchaChannel.buyer : cuchaChannel.seller;
    const receiver: CuchaUser = isTheSenderTheBuyer ? cuchaChannel.seller : cuchaChannel.buyer;

    setSenderBan({
      ...senderBan,
      global: {
        banned: sender.globalBlock,
        banDuration: sender.ban?.duration,
        expiry: sender.ban?.expiresAt,
      },
    });

    setReceiverBan({
      ...receiverBan,
      global: {
        banned: receiver.globalBlock,
        banDuration: receiver.ban?.duration,
        expiry: receiver.ban?.expiresAt,
      },
    });
  };

  const updateGlobalBanStateFromStreamEvent = (event: ExtendedStreamEvent) => {
    const isTempBan = isDefined(event.expiration);
    const isSenderBan = event.user.id === user?.id;

    switch (event.type) {
      case 'user.banned':
        if (isSenderBan) {
          setSenderBan({
            ...senderBan,
            global: {
              banned: true,
              banDuration: isTempBan ? getDifferenceInHoursRounded(event.created_at, event.expiration) : null,
              expiry: isTempBan ? new Date(event.expiration) : null,
            },
          });
        } else {
          setReceiverBan({
            ...receiverBan,
            global: {
              banned: true,
              banDuration: isTempBan ? getDifferenceInHoursRounded(event.created_at, event.expiration) : null,
              expiry: isTempBan ? new Date(event.expiration) : null,
            },
          });
        }
        break;

      case 'user.unbanned':
        if (isSenderBan) {
          setSenderBan({
            ...senderBan,
            global: {
              banned: false,
              banDuration: null,
              expiry: null,
            },
          });
        } else {
          setReceiverBan({
            ...receiverBan,
            global: {
              banned: false,
              banDuration: null,
              expiry: null,
            },
          });
        }
        break;
    }
  };

  const updateChannelBanStateFromStreamEvent = (event: ExtendedStreamEvent) => {
    const isSenderBan = event.user.id === user?.id;
    switch (event.type) {
      case 'user.banned':
        if (isSenderBan) {
          setSenderBan({
            ...senderBan,
            channel: {
              banned: true,
            },
          });
        } else {
          setReceiverBan({
            ...receiverBan,
            channel: {
              banned: true,
            },
          });
        }
        break;

      case 'user.unbanned':
        if (isSenderBan) {
          setSenderBan({
            ...senderBan,
            channel: {
              banned: false,
            },
          });
        } else {
          setReceiverBan({
            ...receiverBan,
            channel: {
              banned: false,
            },
          });
        }
        break;
    }
  };

  const init = (): void => {
    const streamChatClient = new StreamChat(Environment.streamApiKey, {
      baseURL: 'https://chat.stream-chat.com',
      timeout: 6000,
    });

    setStreamChat(streamChatClient);

    streamChatClient.on((event: ExtendedStreamEvent) => {
      const isBanEvent = event.type === 'user.banned' || event.type === 'user.unbanned';

      if (!isBanEvent) {
        return;
      }

      const isChannelBan = isDefined(event.channel_id);

      if (isChannelBan) {
        updateChannelBanStateFromStreamEvent(event);
      } else {
        updateGlobalBanStateFromStreamEvent(event);
      }
    });
  };

  useEffect(() => {
    if (channelId && connectionState) {
      initialiseStreamChannel(streamChat, channelId)
        .then((streamChannel) => {
          setChannel(streamChannel);

          updateChannelBanStateFromStreamCall(streamChannel);

          if (hasGlobalBans(streamChannel)) {
            ChatApiService.getChannel(channelId)
              .then((cuchaChannel) => {
                updateGlobalBanStateFromCuchaCall(cuchaChannel);
              })
              .catch((err) => {
                logger.info(err, `err`);
              });
          }
        })
        .catch((err) => {
          setTokenError(true);
          logger.info(err, `err`);
        });
    }
  }, [channelId, connectionState]);

  useEffect(() => {
    if (streamChat && !connectionState) {
      setIsLoading(true);

      if (user?.id) {
        const loggedInUser = { id: user.id };

        tokenProvider()
          .then((response) => {
            streamChat
              .connectUser(loggedInUser, response.userToken.token)
              .then(() => {
                setIsLoading(false);
                setTokenError(false);
                setConnectionState(true);
              })
              .catch(() => {
                setIsLoading(false);
                setTokenError(true);
                setConnectionState(false);
              });
          })
          .catch(() => {
            setIsLoading(false);
            setTokenError(true);
            setConnectionState(false);
          });
      }
    }
  }, [streamChat]);

  // Init chat on mount
  useEffect(() => {
    (async () => {
      try {
        init();
        // payload required for create channel VC api
        const payload = {
          buyerId: getBuyerId(),
          sellerId: seller?.id,
          productId: product?.id,
        };

        if (payload.buyerId && payload.productId) {
          const cuchaChannel = await createChannel(payload);
          setBuyerLocation(cuchaChannel?.buyer?.countryName);
          setChannelId(cuchaChannel.id);
          setBuyerId(cuchaChannel?.buyer?.id);
          setChannelError(false);
        }
      } catch (e) {
        setIsLoading(false);
        setChannelError(true);
        logger.info(e, `err`);
      }
    })();
    return () => {
      chatLogout();
    };
  }, []);

  const value: ChatContextState = {
    init,
    streamChat,
    channel,
    channelError,
    tokenError,
    setChannel,
    channelId,
    buyerId,
    setChannelId,
    chatWrapper,
    messages,
    setMessages,
    msgBeenRead,
    setMsgBeenRead,
    transformMessages,
    messagesEnding,
    scrollToBottom,
    createChannel,
    banUserFromChannel,
    unbanUserFromChannel,
    sendMessage,
    handleSendingMessage,
    translateMessage,
    translateMessagePromise,
    markAsRead,
    chatLogout,
    connectionState,
    setConnectionState,
    tempStatus,
    isLoading,
    setIsLoading,
    senderBan,
    setSenderBan,
    receiverBan,
    product,
    mode,
    isSeller,
    buyerLocation,
    isProductMissing,
    isProductProhibited,
    errorChatId,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

const useChat = () => useContext(ChatContext);

export { ChatProvider, useChat };
